import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: string;
  placement?: "top" | "bottom" | "left" | "right";
  children: React.ReactNode;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  placement = "top",
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
    translateX: string;
  }>({ top: 0, left: 0, translateX: "-50%" });
  const triggerRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!isVisible || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    let top = rect.top;
    let left = rect.left + rect.width / 2;
    let translateX = "-50%";

    // Adjust for left edge
    if (left < 100) {
      left = rect.left;
      translateX = "0";
    }
    // Adjust for right edge
    else if (viewportWidth - left < 100) {
      left = rect.right;
      translateX = "-100%";
    }

    if (placement === "top") {
      top -= 8;
    } else if (placement === "bottom") {
      top += rect.height + 8;
    }

    setTooltipPosition({ top, left, translateX });
  };

  useEffect(() => {
    updatePosition();

    // Update position on scroll and resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isVisible, placement]);

  const tooltipStyles = `
    fixed z-[9999] px-3 py-2 text-sm text-white bg-zinc-800/85 rounded shadow-lg 
    max-w-[300px] text-center pointer-events-none
    ${placement === "top" ? "-translate-y-full" : ""}
  `;

  return (
    <>
      <div
        ref={triggerRef}
        className="relative inline-block"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {isVisible &&
        createPortal(
          <div
            className={tooltipStyles}
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: `${tooltipPosition.translateX} translateY(-100%)`,
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};
